/**
 * CoordinatorCard — one card per coordinator: time per study under the
 * current lens (bar rows), delegation / team / portal-access chips, and the
 * opportunity-gap list (authorized but little or no logged time).
 */

import { AlertTriangle, FileSignature, KeyRound, Users } from 'lucide-react';
import { TimeLens, formatMinutes } from '../types';
import { CoordinatorRollup, lensTotal, workedStudyKeys } from '../hooks/useInsightsData';
import { StatusBreakdownBar } from './StatusBreakdownBar';

export interface CoordinatorStudyLink {
  studyKey: string;
  label: string; // nickname or protocol number (or raw string for unmapped)
  delegated: boolean; // currently delegated (eReg)
  delegationPending: boolean; // entry exists but not yet active
  onTeam: boolean; // active Site Organizer team member
  teamRole: string | null;
  portalCount: number; // portal-access assignments on this study
}

interface CoordinatorCardProps {
  displayName: string;
  subtitle: string | null;
  rollup: CoordinatorRollup | null;
  lens: TimeLens;
  /** studyKey -> display label for bar rows. */
  studyLabels: Map<string, string>;
  /** Studies this person is authorized on (delegation/team/portal). */
  links: CoordinatorStudyLink[];
  /** Max per-study lens minutes across the page (bar scaling). */
  maxStudyMinutes: number;
  isUnattributed?: boolean;
  isUnmappedName?: boolean;
  /** False when the effort/visits fetch for this range failed: worked time is
   *  UNKNOWN, not zero, so the gap list must not accuse anyone. */
  activityKnown?: boolean;
}

export function CoordinatorCard({
  displayName,
  subtitle,
  rollup,
  lens,
  studyLabels,
  links,
  maxStudyMinutes,
  isUnattributed = false,
  isUnmappedName = false,
  activityKnown = true,
}: CoordinatorCardProps) {
  const total = rollup ? lensTotal(rollup.minutesByStatus, lens) : 0;

  const studyRows = rollup
    ? [...rollup.studyStatusMinutes.entries()]
        .map(([key, statusMinutes]) => ({
          key,
          label: studyLabels.get(key) || key.replace(/^rawstudy:/, ''),
          minutes: lensTotal(statusMinutes, lens),
          statusMinutes,
        }))
        .filter((r) => r.minutes > 0)
        .sort((a, b) => b.minutes - a.minutes)
    : [];

  // "Worked" is deliberately NOT derived from `studyRows` above: those rows are
  // lens-filtered, so on the default `with_crc` lens a study this person logged
  // only lobby/investigator time on would drop out and land in the gap list —
  // accusing them of ignoring a study they demonstrably worked. The gap
  // contract is "authorized minus studies with logged time" (insights/CLAUDE.md),
  // so membership uses whole-visit minutes across every status. The displayed
  // rows stay lens-scoped.
  const workedKeys = workedStudyKeys(rollup);
  const gaps = activityKnown
    ? links.filter((l) => (l.delegated || l.onTeam || l.portalCount > 0) && !workedKeys.has(l.studyKey))
    : [];

  // A delegation entry with all-false flags (ended, or not yet started, with
  // no team/portal footing either) shouldn't render as a bare authorization
  // chip — only show links with at least one positive flag.
  const visibleLinks = links.filter((l) => l.delegated || l.delegationPending || l.onTeam || l.portalCount > 0);

  // Any minutes recorded under an unmapped study name (rawstudy:*)? The gap
  // list can't match those to a study:<protocol_id> authorization link, so
  // it may falsely accuse this coordinator of a gap — caveat when shown.
  const hasUnmappedStudyMinutes = rollup
    ? [...rollup.studyStatusMinutes.keys()].some((k) => k.startsWith('rawstudy:'))
    : false;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{displayName}</p>
          {subtitle && <p className="truncate text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatMinutes(total)}</span>
          <span
            className="text-xs text-gray-500 dark:text-gray-400"
            title="completed / scheduled (scheduled includes cancellations and no-shows)"
          >
            {rollup?.completed_count || 0}/{rollup?.visit_count || 0} visit{(rollup?.visit_count || 0) === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {isUnattributed && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          Visits with no CRC recorded — this time can't be attributed to anyone.
        </p>
      )}
      {isUnmappedName && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          CRC name not linked to a user account yet — map it in Status Board Admin → CRC Mapping.
        </p>
      )}

      {/* Per-study time bars */}
      {studyRows.length > 0 && (
        <div className="mt-3 space-y-2">
          {studyRows.map((row) => (
            <div key={row.key}>
              <div className="mb-0.5 flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-medium text-gray-700 dark:text-gray-200">{row.label}</span>
                <span className="flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                  {formatMinutes(row.minutes)}
                </span>
              </div>
              <StatusBreakdownBar
                minutesByStatus={row.statusMinutes}
                scale={maxStudyMinutes > 0 ? row.minutes / maxStudyMinutes : 0}
              />
            </div>
          ))}
        </div>
      )}

      {/* Authorization chips */}
      {visibleLinks.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {visibleLinks.map((l) => (
            <span
              key={l.studyKey}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              title={[
                l.delegated ? 'Currently delegated (eReg DOA)' : l.delegationPending ? 'Delegation pending sign-off' : null,
                l.onTeam ? `Study team${l.teamRole ? `: ${l.teamRole}` : ''}` : null,
                l.portalCount > 0 ? `${l.portalCount} portal login${l.portalCount === 1 ? '' : 's'} granted` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              {l.label}
              {(l.delegated || l.delegationPending) && (
                <FileSignature
                  size={12}
                  aria-label={l.delegated ? 'Delegated' : 'Delegation pending'}
                  className={l.delegated ? 'text-green-600 dark:text-green-400' : 'text-amber-500 dark:text-amber-400'}
                />
              )}
              {l.onTeam && <Users size={12} aria-label="Study team member" className="text-primary-600 dark:text-primary-400" />}
              {l.portalCount > 0 && <KeyRound size={12} aria-label="Portal access granted" className="text-gray-500 dark:text-gray-400" />}
            </span>
          ))}
        </div>
      )}

      {/* Opportunity gaps */}
      {gaps.length > 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-2.5 dark:border-amber-700 dark:bg-amber-900/10">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle size={13} aria-hidden="true" />
            Could spend time here
          </p>
          <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
            {gaps.map((g) => g.label).join(', ')} — authorized (delegated, on the team, or holding portal access) but no
            visit time in this range.
          </p>
          {hasUnmappedStudyMinutes && (
            <p className="mt-1 text-xs text-amber-700/70 dark:text-amber-400/70">
              Some visit time is recorded under unmapped study names and can't be matched to studies.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default CoordinatorCard;
